import { VideoJsBridge, useVideoJs } from "@/pages-app/player/[id]/VideoJsBridge";
import { isPlayerDisposed, safePlay } from "@/lib/videojs-setup";
import closeSquareIcon from "@public/img/close-square.svg";
import forwardIcon from "@public/img/player-forward.svg";
import fullscreenExitIcon from "@public/img/player-fullscreen-exit.svg";
import fullscreenIcon from "@public/img/player-fullscreen.svg";
import pauseIcon from "@public/img/player-pause.svg";
import playIcon from "@public/img/player-play.svg";
import redoIcon from "@public/img/player-redo.svg";
import volumeCrossIcon from "@public/img/player-volume-cross.svg";
import volumeIcon from "@public/img/player-volume.svg";
import volumeLoudIcon from "@public/img/player-volume-loud.svg";
import volumeSmallIcon from "@public/img/player-volume-small.svg";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { VideoJsPlayer as Player } from "@/types/videojs-player";
import {
  bindPlayerInteractionEvents,
  getFullscreenElement,
  isFiniteNumber,
  onFullscreenChange,
  padTwoDigits,
  supportsHoverMediaQuery,
  toggleElementFullscreen,
} from "@/utils/legacy-dom-events";
import "@/styles/trailer-player.css";

var CONTROLS_HIDE_DELAY_MS = 4000;

function formatPlayerTime(seconds: number): string {
  if (!isFiniteNumber(seconds) || seconds < 0) {
    return "0:00";
  }
  var h = Math.floor(seconds / 3600);
  var m = Math.floor((seconds % 3600) / 60);
  var s = Math.floor(seconds % 60);
  if (h > 0) {
    return h + ":" + padTwoDigits(m) + ":" + padTwoDigits(s);
  }
  return m + ":" + padTwoDigits(s);
}

function durationOrZero(p: Player): number {
  var du = p.duration();
  return isFiniteNumber(du) ? du : 0;
}

function getPlayerTime(p: Player): number {
  var t = p.currentTime();
  return isFiniteNumber(t) ? t : 0;
}

function getVolumeIcon(muted: boolean, volume: number): string {
  if (muted || volume === 0) {
    return volumeCrossIcon;
  }
  if (volume > 0.5) {
    return volumeLoudIcon;
  }
  if (volume > 0.25) {
    return volumeSmallIcon;
  }
  return volumeIcon;
}

function useTrailerPlayerChrome(player: Player | null, wrapperRef: React.RefObject<HTMLDivElement | null>) {
  var pausedState = useState(true);
  var paused = pausedState[0];
  var setPaused = pausedState[1];
  var mutedState = useState(false);
  var muted = mutedState[0];
  var setMuted = mutedState[1];
  var currentState = useState(0);
  var current = currentState[0];
  var setCurrent = currentState[1];
  var durationState = useState(0);
  var duration = durationState[0];
  var setDuration = durationState[1];
  var volumeState = useState(1);
  var volume = volumeState[0];
  var setVolume = volumeState[1];
  var fullscreenState = useState(false);
  var fullscreen = fullscreenState[0];
  var setFullscreen = fullscreenState[1];
  var showControlsState = useState(true);
  var showControls = showControlsState[0];
  var setShowControls = showControlsState[1];
  var hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  var scrubWasPlayingRef = useRef(false);

  var clearHideTimer = useCallback(function () {
    if (hideTimerRef.current != null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  var revealControls = useCallback(function () {
    setShowControls(true);
    clearHideTimer();
    var p = player;
    if (p && !isPlayerDisposed(p) && !p.paused()) {
      hideTimerRef.current = setTimeout(function () {
        setShowControls(false);
      }, CONTROLS_HIDE_DELAY_MS);
    }
  }, [clearHideTimer, player]);

  useEffect(function () {
    var p = player;
    if (!p || isPlayerDisposed(p)) {
      return;
    }

    var activePlayer = p;

    var refreshUi = function () {
      setPaused(Boolean(activePlayer.paused()));
      setMuted(Boolean(activePlayer.muted()));
      var v = activePlayer.volume();
      if (isFiniteNumber(v)) {
        setVolume(v);
      }
      setCurrent(getPlayerTime(activePlayer));
      setDuration(durationOrZero(activePlayer));
    };

    refreshUi();
    var ev = ["play", "playing", "pause", "volumechange", "timeupdate", "loadedmetadata", "durationchange"];
    ev.forEach(function (e) {
      activePlayer.on(e, refreshUi);
    });
    return function () {
      ev.forEach(function (e) {
        activePlayer.off(e, refreshUi);
      });
    };
  }, [player]);

  useEffect(function () {
    return onFullscreenChange(function () {
      setFullscreen(Boolean(getFullscreenElement()));
    });
  }, []);

  useEffect(function () {
    var p = player;
    if (!p || isPlayerDisposed(p)) {
      return;
    }

    var activePlayer = p;

    var onPause = function () {
      clearHideTimer();
      setShowControls(true);
    };
    var onPlay = function () {
      revealControls();
    };

    activePlayer.on("pause", onPause);
    activePlayer.on("play", onPlay);
    if (activePlayer.paused()) {
      setShowControls(true);
    } else {
      revealControls();
    }

    return function () {
      activePlayer.off("pause", onPause);
      activePlayer.off("play", onPlay);
      clearHideTimer();
    };
  }, [clearHideTimer, player, revealControls]);

  useEffect(function () {
    var shell = wrapperRef.current;
    if (!shell || !player) {
      return;
    }

    return bindPlayerInteractionEvents(shell, {
      onMove: function () {
        if (!supportsHoverMediaQuery()) {
          return;
        }
        revealControls();
      },
      onDown: function () {
        revealControls();
      },
    });
  }, [player, revealControls, wrapperRef]);

  var safeDuration = duration > 0 ? duration : 0;

  return {
    paused: paused,
    muted: muted,
    current: current,
    safeDuration: safeDuration,
    volume: volume,
    fullscreen: fullscreen,
    showControls: showControls,
    scrubWasPlayingRef: scrubWasPlayingRef,
    timeRailPercent: safeDuration > 0 ? Math.min(100, (current / safeDuration) * 100) : 0,
    volRailPercent: (muted ? 0 : volume) * 100,
    revealControls: revealControls,
  };
}

function TrailerControls({ title }: { title: string }) {
  var videoJs = useVideoJs();
  var player = videoJs.player;
  var wrapperRef = videoJs.wrapperRef;
  var chrome = useTrailerPlayerChrome(player, wrapperRef);

  var rangeMax = chrome.safeDuration || 100;
  var rangeValue = chrome.safeDuration ? Math.min(chrome.current, chrome.safeDuration) : 0;

  var controlsClass = "trailer-controls";
  if (!chrome.showControls && !chrome.paused) {
    controlsClass = controlsClass + " trailer-controls--hidden";
  }

  var beginScrub = function () {
    var p = player;
    if (!p || isPlayerDisposed(p)) {
      return;
    }
    chrome.scrubWasPlayingRef.current = !p.paused();
    p.pause();
  };

  var endScrub = function () {
    var p = player;
    if (!p || isPlayerDisposed(p)) {
      return;
    }
    if (chrome.scrubWasPlayingRef.current) {
      safePlay(p);
    }
  };

  return (
    <div className={controlsClass}>
      <div className="trailer-controls__top">
        <p className="trailer-controls__movie-title">{title}</p>
      </div>

      {chrome.paused && (
        <div className="trailer-controls__center-play">
          <button
            type="button"
            className="trailer-controls__play-btn"
            onClick={function () {
              var p = player;
              if (!p || isPlayerDisposed(p)) {
                return;
              }
              safePlay(p);
            }}
          >
            <img src={playIcon} alt="play" />
          </button>
        </div>
      )}

      <div className="trailer-controls__bottom">
        <div className="trailer-controls__time-row">
          <span className="trailer-controls__time">{formatPlayerTime(chrome.current)}</span>
          <div className="trailer-controls__scrub">
            <input
              aria-label="زمان پخش"
              className="trailer-controls__scrub-input"
              disabled={!chrome.safeDuration}
              max={rangeMax}
              min={0}
              step={0.05}
              type="range"
              value={rangeValue}
              onChange={function (e) {
                var p = player;
                if (!p || isPlayerDisposed(p)) {
                  return;
                }
                p.currentTime(Number(e.target.value));
              }}
              onMouseDown={beginScrub}
              onTouchStart={beginScrub}
              onMouseUp={endScrub}
              onTouchEnd={endScrub}
            />
            <div className="trailer-controls__scrub-rail">
              <div className="trailer-controls__scrub-fill" style={{ width: chrome.timeRailPercent + "%" }}></div>
            </div>
          </div>
          <span className="trailer-controls__time trailer-controls__time--end">
            {formatPlayerTime(chrome.safeDuration)}
          </span>
        </div>

        <div className="trailer-controls__actions">
          <div className="trailer-controls__actions-left">
            <button
              type="button"
              className="trailer-controls__icon-btn"
              onClick={function () {
                var shell = wrapperRef.current;
                if (!shell) {
                  return;
                }
                toggleElementFullscreen(shell);
              }}
            >
              <img src={chrome.fullscreen ? fullscreenExitIcon : fullscreenIcon} alt="fullscreen" />
            </button>
            <div className="trailer-controls__volume-group">
              <button
                type="button"
                className="trailer-controls__icon-btn"
                onClick={function () {
                  var p = player;
                  if (!p || isPlayerDisposed(p)) {
                    return;
                  }
                  p.muted(!p.muted());
                }}
              >
                <img src={getVolumeIcon(chrome.muted, chrome.volume)} alt="volume" />
              </button>
              <div className="trailer-controls__volume-scrub">
                <input
                  aria-label="صدا"
                  className="trailer-controls__volume-input"
                  max={1}
                  min={0}
                  step={0.03}
                  type="range"
                  value={chrome.muted ? 0 : chrome.volume}
                  onChange={function (e) {
                    var p = player;
                    if (!p || isPlayerDisposed(p)) {
                      return;
                    }
                    var v = Number(e.target.value);
                    p.volume(v);
                    if (v > 0 && p.muted()) {
                      p.muted(false);
                    }
                  }}
                />
                <div className="trailer-controls__volume-rail">
                  <div className="trailer-controls__volume-fill" style={{ width: chrome.volRailPercent + "%" }}></div>
                </div>
              </div>
            </div>
          </div>

          <div className="trailer-controls__actions-right">
            <div className="trailer-controls__transport">
              <button
                type="button"
                className="trailer-controls__icon-btn"
                onClick={function () {
                  var p = player;
                  if (!p || isPlayerDisposed(p)) {
                    return;
                  }
                  p.currentTime(Math.max(0, getPlayerTime(p) - 10));
                }}
              >
                <img src={redoIcon} alt="backward" />
              </button>
              <button
                type="button"
                className="trailer-controls__icon-btn"
                onClick={function () {
                  var p = player;
                  if (!p || isPlayerDisposed(p)) {
                    return;
                  }
                  if (p.paused()) {
                    safePlay(p);
                  } else {
                    p.pause();
                  }
                }}
              >
                <img src={chrome.paused ? playIcon : pauseIcon} alt="play" />
              </button>
              <button
                type="button"
                className="trailer-controls__icon-btn"
                onClick={function () {
                  var p = player;
                  if (!p || isPlayerDisposed(p)) {
                    return;
                  }
                  var t = getPlayerTime(p);
                  var du = p.duration();
                  var end = isFiniteNumber(du) ? du : t + 10;
                  p.currentTime(Math.min(end, t + 10));
                }}
              >
                <img src={forwardIcon} alt="forward" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type TrailerPlayerDialogProps = {
  title: string;
  trailerUrl: string;
  onClose: () => void;
};

export default function TrailerPlayerDialog({ title, trailerUrl, onClose }: TrailerPlayerDialogProps) {
  var mimeType: "application/x-mpegurl" | "video/mp4" =
    trailerUrl.indexOf(".m3u8") >= 0 ? "application/x-mpegurl" : "video/mp4";

  useEffect(function () {
    var prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return function () {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(function () {
    var onKey = function (e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return function () {
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div className="trailer-modal-overlay" onClick={onClose}>
      <div className="trailer-modal-overlay__wrap">
        <div className="trailer-modal-overlay__cell">
          <div
            className="trailer-modal"
            onClick={function (e) {
              e.stopPropagation();
            }}
            dir="rtl"
          >
            <div className="trailer-modal__header">
              <p className="trailer-modal__title">تریلر</p>
              <button type="button" className="trailer-modal__close" onClick={onClose}>
                <img src={closeSquareIcon} alt="بستن" />
              </button>
            </div>

            <div className="trailer-modal__video">
              <VideoJsBridge
                wrapClassName="trailer-player-host"
                mediaSrc={trailerUrl}
                mimeType={mimeType}
                subtitles={[]}
                videoClassNames="vjs-big-play-centered vjs-fill ratio-contain"
              >
                <TrailerControls title={title} />
              </VideoJsBridge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
